import React, { ReactNode, useState } from 'react';
import { Card, Table as ReactTable } from 'react-bootstrap';
import './Table.scss';
import { Spinner } from '../feedback/Spinner';
import { faArrowUpToLine } from '@fortawesome/pro-regular-svg-icons';
import { SalvButton, Size } from '@salvapp/ui-components';
import { scroller } from '../common/scroller';

interface TableInterface<Item> {
  total: number;
  page: number;
  size: number;
  showLoadMoreUntilNoDataToFetch?: boolean;
  initialItems: Item[];
  body: (items: Item[]) => React.ReactNode;
  header: (items: Item[]) => React.ReactNode;
  fetchPage: (page: number, size: number) => Promise<Item[] | undefined>;
  titleActions?: ReactNode;
  title?: string;
  subTitle?: string | undefined;
  borderless?: boolean;
  striped?: boolean;
  className?: string;
}

export const LoadMoreTable = <T,>({
  total,
  page,
  size,
  showLoadMoreUntilNoDataToFetch = false,
  initialItems,
  header,
  body,
  titleActions,
  title,
  subTitle,
  borderless = false,
  striped = false,
  fetchPage,
  className,
}: TableInterface<T>): React.JSX.Element => {
  const [fetching, setFetching] = useState(false);
  const [offset, setOffset] = useState(page);
  const [items, setItems] = useState<T[]>(initialItems);
  const initialItemsNotLessThanTotal = !(initialItems.length < total);
  const initialItemsNotLessThanPageSize = initialItems.length >= size;
  //To avoid cases when total value might be incorrect, show Load More always when initial/fetched size >= page size
  const [hasMoreItems, setHasMoreItems] = useState(
    initialItemsNotLessThanTotal && initialItemsNotLessThanPageSize && showLoadMoreUntilNoDataToFetch
  );

  const fetchData = async () => {
    const moreItems = await fetchPage(offset + 1, size);
    setHasMoreItems(showLoadMoreUntilNoDataToFetch && moreItems?.length === size);
    if (moreItems) {
      setItems([...items, ...moreItems]);
      setOffset(offset + 1);
    }
  };

  const loadMore = () => {
    setFetching(true);
    fetchData().finally(() => {
      setFetching(false);
    });
  };

  return (
    <React.Fragment>
      <Card
        id={title}
        body={false}
        className={`${borderless ? 'no-border' : undefined} ${className} load-more-table`}
        data-testid='load-more-table-card'
      >
        <Card.Header data-testid='load-more-table-header' className='d-block'>
          {title && (
            <h4 className='card-header-title' style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
              <span>
                {title} • {items.length} of {total}
              </span>
              {titleActions}
            </h4>
          )}
          {subTitle && <p className='subtitle m-0'>{subTitle}</p>}
        </Card.Header>
        {items.length > 0 ? (
          <>
            <ReactTable hover responsive size='sm' className='card-table' striped={striped}>
              <thead>{header(items)}</thead>
              <tbody>{body(items)}</tbody>
            </ReactTable>
            <Card.Footer className={items.length < total ? 'load-more-footer' : ''}>
              {fetching ? (
                <div className='row align-items-center justify-content-center'>
                  <Spinner loading />
                </div>
              ) : (
                <div className='row load-more align-items-center justify-content-between'>
                  Showing {items.length} of {total} matches
                  <div className='load-more-buttons'>
                    {(items.length < total || hasMoreItems) && (
                      <SalvButton label={'Load more'} onClick={loadMore} variant={'primary'} size={Size.SMALL} />
                    )}
                    {title && (
                      <SalvButton
                        label={'Back to top'}
                        icon={faArrowUpToLine}
                        variant={'secondary'}
                        size={Size.SMALL}
                        onClick={() =>
                          scroller.scrollTo(title, {
                            smooth: true,
                            duration: 400,
                            offset: -20,
                          })
                        }
                      />
                    )}
                  </div>
                </div>
              )}
            </Card.Footer>
          </>
        ) : (
          <Card.Body>
            <div className='small'>Nothing to show here, yet.</div>
          </Card.Body>
        )}
      </Card>
    </React.Fragment>
  );
};

export default LoadMoreTable;
